import { getTopSetProgressOptions } from "@/actions/workout/queries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import type {
  WeeklyTopSetLift,
  WeeklyTopSetProgress,
} from "@/utils/top-set-progress";
import { kgToUnit, useWeightUnit } from "@/utils/weight-unit";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import {
  AlertTriangle,
  BarChart3,
  Check,
  CircleDashed,
  Trophy,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const liftMeta = {
  squat: { label: "Squat", color: "#ef4444" },
  bench_press: { label: "Bench Press", color: "#3b82f6" },
  deadlift: { label: "Deadlift", color: "#8b5cf6" },
} as const;

type LiftKey = keyof typeof liftMeta;

function displayWeight(weight: number | null, unit: "kg" | "lbs") {
  if (weight == null) return "—";
  const converted = kgToUnit(weight, unit);
  return converted == null ? "—" : `${Number(converted.toFixed(1))} ${unit}`;
}

function LiftCard({
  lift,
  liftType,
  unit,
}: {
  lift: WeeklyTopSetLift | null;
  liftType: LiftKey;
  unit: "kg" | "lbs";
}) {
  const meta = liftMeta[liftType];
  const dayLabel = lift?.dayName
    ? lift.dayName
    : lift?.dayNumber != null
      ? `Gün ${lift.dayNumber}`
      : null;
  return (
    <div className="rounded-xl border border-border/60 bg-background/70 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="font-semibold text-sm">{meta.label}</span>
        {lift ? (
          <Check className="h-4 w-4 text-emerald-500" />
        ) : (
          <CircleDashed className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <p className="font-black text-2xl tabular-nums tracking-tight">
        {displayWeight(lift?.weight ?? null, unit)}
      </p>
      {lift ? (
        <div className="mt-2 space-y-1 text-muted-foreground text-xs">
          <p>
            {lift.reps ?? "—"} tekrar
            {lift.rpe != null ? ` · RPE ${lift.rpe}` : ""}
          </p>
          <p>
            {format(new Date(lift.loggedAt), "d MMM yyyy", { locale: tr })}
            {dayLabel ? ` · ${dayLabel}` : ""}
          </p>
        </div>
      ) : (
        <p className="mt-2 text-muted-foreground text-xs">Henüz kaydedilmedi</p>
      )}
    </div>
  );
}

export function TopSetProgressPanel({ userId }: { userId?: string }) {
  const { unit } = useWeightUnit();
  const { data, isLoading, isError } = useQuery(
    getTopSetProgressOptions(userId),
  );
  const body = data?.status === 200 ? data.body : null;
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [visibleSeries, setVisibleSeries] = useState<Set<string>>(
    () => new Set(["total"]),
  );

  useEffect(() => {
    if (!body || selectedProgramId) return;
    setSelectedProgramId(
      body.activeProgram?.programId ?? body.programs[0]?.programId ?? "",
    );
  }, [body, selectedProgramId]);

  const weeks = useMemo(() => {
    if (!body) return [];
    return body.weeks
      .filter((week) => week.programId === selectedProgramId)
      .sort((a, b) => a.weekNumber - b.weekNumber) as WeeklyTopSetProgress[];
  }, [body, selectedProgramId]);

  const currentWeekNumber =
    body?.activeProgram?.programId === selectedProgramId
      ? body.activeProgram.currentWeek
      : (weeks.at(-1)?.weekNumber ?? 1);
  const currentWeek = weeks.find(
    (week) => week.weekNumber === currentWeekNumber,
  ) ?? {
    programId: selectedProgramId,
    programName:
      body?.programs.find((program) => program.programId === selectedProgramId)
        ?.name ?? "Program",
    weekNumber: currentWeekNumber,
    lifts: { squat: null, bench_press: null, deadlift: null },
    completedCount: 0,
    partialTotal: 0,
    total: null,
    lastLoggedAt: null,
  };

  const chartData = weeks.map((week) => ({
    week: `H${week.weekNumber}`,
    total: week.total == null ? null : kgToUnit(week.total, unit),
    squat: kgToUnit(week.lifts.squat?.weight, unit),
    bench_press: kgToUnit(week.lifts.bench_press?.weight, unit),
    deadlift: kgToUnit(week.lifts.deadlift?.weight, unit),
  }));
  const missingLiftLabels = (Object.keys(liftMeta) as LiftKey[])
    .filter((liftType) => !currentWeek.lifts[liftType])
    .map((liftType) => liftMeta[liftType].label);

  const toggleSeries = (series: string) => {
    setVisibleSeries((current) => {
      const next = new Set(current);
      if (next.has(series)) next.delete(series);
      else next.add(series);
      return next;
    });
  };

  if (isLoading) return <Skeleton className="h-[520px] w-full rounded-xl" />;
  if (isError || !body) {
    return (
      <Card className="border-border/60 shadow-none">
        <CardContent className="py-10 text-center text-muted-foreground text-sm">
          Kuvvet takibi yüklenemedi.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden border-border/60 shadow-none">
        <CardHeader className="border-border/50 border-b bg-muted/20">
          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Trophy className="h-5 w-5 text-primary" /> Haftalık Top Set
                Totali
              </CardTitle>
              <p className="mt-1 text-muted-foreground text-xs">
                Tekrar sayısından bağımsız, tamamlanan en ağır SBD top setleri.
              </p>
            </div>
            {body.programs.length > 1 && (
              <Select
                value={selectedProgramId}
                onValueChange={setSelectedProgramId}
              >
                <SelectTrigger className="w-full sm:w-[240px]">
                  <SelectValue placeholder="Program seçin" />
                </SelectTrigger>
                <SelectContent>
                  {body.programs.map((program) => (
                    <SelectItem
                      key={program.programId}
                      value={program.programId}
                    >
                      {program.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5 p-4 sm:p-6">
          <div className="flex flex-col justify-between gap-3 rounded-xl bg-primary/5 p-4 sm:flex-row sm:items-center">
            <div>
              <Badge variant="outline">Hafta {currentWeek.weekNumber}</Badge>
              <p className="mt-2 text-muted-foreground text-xs">
                {currentWeek.completedCount}/3 kaldırış tamamlandı
              </p>
            </div>
            <div className="sm:text-right">
              <p className="text-muted-foreground text-xs uppercase tracking-wider">
                {currentWeek.total == null ? "Ara toplam" : "Haftalık total"}
              </p>
              <p className="font-black text-3xl text-primary tabular-nums tracking-tight">
                {displayWeight(
                  currentWeek.total ?? currentWeek.partialTotal,
                  unit,
                )}
              </p>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            {(Object.keys(liftMeta) as LiftKey[]).map((liftType) => (
              <LiftCard
                key={liftType}
                lift={currentWeek.lifts[liftType]}
                liftType={liftType}
                unit={unit}
              />
            ))}
          </div>

          {missingLiftLabels.length > 0 && (
            <div className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-amber-900 text-xs dark:text-amber-100">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Bu hafta eksik top set: {missingLiftLabels.join(", ")}. Total,
                üç kaldırış tamamlandığında oluşur.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/60 shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4 text-primary" /> Total Gelişimi
          </CardTitle>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              type="button"
              size="sm"
              variant={visibleSeries.has("total") ? "default" : "outline"}
              onClick={() => toggleSeries("total")}
            >
              Total
            </Button>
            {(Object.keys(liftMeta) as LiftKey[]).map((liftType) => (
              <Button
                key={liftType}
                type="button"
                size="sm"
                variant={visibleSeries.has(liftType) ? "secondary" : "outline"}
                onClick={() => toggleSeries(liftType)}
              >
                {liftMeta[liftType].label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ left: 0, right: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                  <XAxis dataKey="week" fontSize={11} />
                  <YAxis width={48} fontSize={11} unit={unit} />
                  <Tooltip
                    formatter={(value) => [`${value} ${unit}`, ""]}
                    contentStyle={{
                      borderRadius: 10,
                      borderColor: "hsl(var(--border))",
                      background: "hsl(var(--background))",
                    }}
                  />
                  {visibleSeries.has("total") && (
                    <Line
                      type="monotone"
                      dataKey="total"
                      name="Total"
                      stroke="hsl(var(--primary))"
                      strokeWidth={3}
                      connectNulls={false}
                    />
                  )}
                  {(Object.keys(liftMeta) as LiftKey[]).map(
                    (liftType) =>
                      visibleSeries.has(liftType) && (
                        <Line
                          key={liftType}
                          type="monotone"
                          dataKey={liftType}
                          name={liftMeta[liftType].label}
                          stroke={liftMeta[liftType].color}
                          strokeWidth={2}
                        />
                      ),
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground text-sm">
              İlk top setler kaydedildiğinde gelişim grafiği burada oluşacak.
            </div>
          )}
        </CardContent>
      </Card>

      {weeks.length > 0 && (
        <Card className="border-border/60 shadow-none">
          <CardHeader>
            <CardTitle className="text-base">Hafta Geçmişi</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {[...weeks].reverse().map((week) => (
              <details
                key={`${week.programId}-${week.weekNumber}`}
                className="group rounded-lg border border-border/50 p-3"
              >
                <summary className="flex cursor-pointer list-none flex-col justify-between gap-2 sm:flex-row sm:items-center">
                  <div>
                    <p className="font-semibold text-sm">
                      Hafta {week.weekNumber}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Squat{" "}
                      {displayWeight(week.lifts.squat?.weight ?? null, unit)} ·
                      Bench{" "}
                      {displayWeight(
                        week.lifts.bench_press?.weight ?? null,
                        unit,
                      )}{" "}
                      · Deadlift{" "}
                      {displayWeight(week.lifts.deadlift?.weight ?? null, unit)}
                    </p>
                  </div>
                  <div className="sm:text-right">
                    <p className="font-bold tabular-nums">
                      {displayWeight(week.total ?? week.partialTotal, unit)}
                    </p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                      {week.total == null
                        ? `${week.completedCount}/3 · Ara toplam`
                        : "Tamamlandı"}
                    </p>
                  </div>
                </summary>
                <div className="mt-3 grid gap-3 border-border/50 border-t pt-3 md:grid-cols-3">
                  {(Object.keys(liftMeta) as LiftKey[]).map((liftType) => (
                    <LiftCard
                      key={liftType}
                      lift={week.lifts[liftType]}
                      liftType={liftType}
                      unit={unit}
                    />
                  ))}
                </div>
              </details>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
